import React, { useState, useEffect, useCallback } from 'react';
import chemicalService from '../../../services/chemicalService';

// Tax type id = 5 (Tax on sale label index 0)
const TAX_TYPE_ID = 5;
const TAX_ON_SALE_LABEL = 'Tax on sale';
const TAX_ON_SALE_RATE = 0.10; // 10%

const InvoiceExpenseFormModal = ({ onClose, onSaved }) => {
  const [minimized, setMinimized] = useState(false);
  const [maximized, setMaximized] = useState(false);

  const [products, setProducts] = useState([]);
  const [expenseTypes, setExpenseTypes] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [saleLines, setSaleLines] = useState([]);

  const [productId, setProductId] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [saleItemId, setSaleItemId] = useState('');
  const [detail, setDetail] = useState(null);

  const [linesByType, setLinesByType] = useState({});

  const [loadingMaster, setLoadingMaster] = useState(true);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [loadingLines, setLoadingLines] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        setLoadingMaster(true);
        const [pRes, tRes] = await Promise.all([
          chemicalService.getProducts({ page: 1, limit: 1000 }),
          chemicalService.getExpenseTypes({ page: 1, limit: 500 }),
        ]);
        setProducts(pRes.data || []);
        setExpenseTypes((tRes.data || []).filter((t) => t.is_active));
      } catch (err) {
        console.error('Error loading master data:', err);
        alert('❌ Failed to load products or expense types');
      } finally {
        setLoadingMaster(false);
      }
    };
    load();
  }, []);

  const buildEmptyLines = useCallback((types, det) => {
    const map = {};
    for (const t of types) {
      if (t.fixed_labels) {
        map[t.id] = t.fixed_labels.split('|').map((lbl) => {
          // Auto-fill Tax on sale = 10% of line amount (without GST) when invoice has GST
          if (
            det &&
            parseInt(t.id) === TAX_TYPE_ID &&
            lbl.trim() === TAX_ON_SALE_LABEL &&
            parseFloat(det.invoice_gst_amount) > 0
          ) {
            const autoAmount = Math.round(parseFloat(det.line_amount) * TAX_ON_SALE_RATE * 100) / 100;
            return { label: lbl.trim(), amount: String(autoAmount), notes: '' };
          }
          return { label: lbl.trim(), amount: '', notes: '' };
        });
      } else {
        map[t.id] = [{ label: '', amount: '', notes: '' }];
      }
    }
    return map;
  }, []);

  const handleProductChange = async (pid) => {
    setProductId(pid);
    setCustomerId('');
    setSaleItemId('');
    setCustomers([]);
    setSaleLines([]);
    setDetail(null);
    setLinesByType({});
    if (!pid) return;
    try {
      setLoadingCustomers(true);
      const res = await chemicalService.getCustomersForProduct(pid);
      setCustomers(res.data || []);
    } catch (err) {
      console.error('Error loading customers:', err);
    } finally {
      setLoadingCustomers(false);
    }
  };

  const handleCustomerChange = async (cid) => {
    setCustomerId(cid);
    setSaleItemId('');
    setSaleLines([]);
    setDetail(null);
    setLinesByType({});
    if (!cid || !productId) return;
    try {
      setLoadingLines(true);
      const res = await chemicalService.getSaleLinesForProductCustomer(productId, cid);
      setSaleLines(res.data || []);
    } catch (err) {
      console.error('Error loading sale lines:', err);
    } finally {
      setLoadingLines(false);
    }
  };

  const handleSaleLineChange = async (sid) => {
    setSaleItemId(sid);
    setDetail(null);
    setLinesByType({});
    if (!sid) return;
    try {
      setLoadingDetail(true);
      const res = await chemicalService.getSaleLineDetail(sid);
      const d = res.data;
      setDetail(d.detail);

      // Build lines with tax auto-fill
      const map = buildEmptyLines(expenseTypes, d.detail);

      // Overwrite with saved expense rows if any
      if (d.expenses && d.expenses.length > 0) {
        const grouped = {};
        for (const ex of d.expenses) {
          const key = ex.expense_type_id;
          if (!grouped[key]) grouped[key] = [];
          grouped[key].push({ label: ex.label || '', amount: String(ex.amount), notes: ex.notes || '' });
        }
        for (const key of Object.keys(grouped)) map[key] = grouped[key];
      }
      setLinesByType(map);
    } catch (err) {
      console.error('Error loading sale line detail:', err);
      alert('❌ Failed to load invoice detail');
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleLineChange = (typeId, index, field, value) => {
    setLinesByType((prev) => {
      const arr = [...(prev[typeId] || [])];
      arr[index] = { ...arr[index], [field]: value };
      return { ...prev, [typeId]: arr };
    });
  };

  const addLine = (typeId) => {
    setLinesByType((prev) => ({
      ...prev,
      [typeId]: [...(prev[typeId] || []), { label: '', amount: '', notes: '' }],
    }));
  };

  const removeLine = (typeId, index) => {
    setLinesByType((prev) => {
      const arr = [...(prev[typeId] || [])];
      if (arr.length === 1) return prev;
      arr.splice(index, 1);
      return { ...prev, [typeId]: arr };
    });
  };

  // ─── Calculations ───
  const typeTotal = (typeId) =>
    (linesByType[typeId] || []).reduce((s, l) => s + (parseFloat(l.amount) || 0), 0);

  const grandTotal = expenseTypes.reduce((s, t) => s + typeTotal(t.id), 0);

  const lineAmount = detail ? parseFloat(detail.line_amount) || 0 : 0;       // Revenue (no GST)
  const lineGst = detail ? parseFloat(detail.line_gst) || 0 : 0;
  const lineTotalWithGst = detail ? parseFloat(detail.line_total_with_gst) || 0 : 0;
  const cogs = detail ? parseFloat(detail.cogs) || 0 : 0;                    // Cost of goods sold

  // Real Margin = Revenue (no GST) − COGS − All Expenses
  const realMargin = lineAmount - cogs - grandTotal;

  const fmtMoney = (n) => 'Rs ' + (Math.round((parseFloat(n) || 0) * 100) / 100).toLocaleString('en-PK');
  const fmtQty = (n) => parseFloat(n || 0).toFixed(2).replace(/\.?0+$/, '');
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!saleItemId) return alert('❌ Select an invoice first.');

    const lines = [];
    for (const t of expenseTypes) {
      for (const l of (linesByType[t.id] || [])) {
        const amt = parseFloat(l.amount) || 0;
        if (amt <= 0) continue;
        if (!l.label.trim()) return alert(`❌ ${t.name}: every line with an amount needs a label.`);
        lines.push({
          expense_type_id: t.id,
          label: l.label.trim(),
          amount: amt,
          notes: l.notes.trim() || null,
        });
      }
    }

    if (lines.length === 0) return alert('❌ Enter at least one expense amount.');

    try {
      setSubmitting(true);
      await chemicalService.saveInvoiceExpenses({ sale_item_id: parseInt(saleItemId, 10), lines });
      alert('✅ Invoice expenses saved!');
      onSaved();
    } catch (err) {
      alert('❌ ' + (err.response?.data?.message || err.message || 'Failed to save'));
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Modal size style ───
  const getModalStyle = () => {
    if (maximized) return styles.modalMaximized;
    return styles.modal;
  };

  const getOverlayStyle = () => {
    if (minimized) return styles.minimizedBar;
    if (maximized) return styles.overlayMaximized;
    return styles.modalOverlay;
  };

  return (
    <div style={getOverlayStyle()}>
      <div
        style={minimized ? styles.minimizedModal : getModalStyle()}
        onClick={minimized ? () => setMinimized(false) : (e) => e.stopPropagation()}
      >
        {minimized ? (
          <div style={styles.minimizedContent}>
            <span style={{ fontWeight: 700, color: '#2c3e50' }}>🧾 Invoice Expense</span>
            <span style={{ fontSize: 12, color: '#7f8c8d' }}>Click to restore</span>
            <button onClick={(e) => { e.stopPropagation(); onClose(); }} style={styles.closeButton}>✕</button>
          </div>
        ) : (
          <>
            <div style={styles.modalHeader}>
              <div>
                <h2 style={styles.modalTitle}>🧾 Expense for Specific Product</h2>
                <p style={{ color: '#7f8c8d', fontSize: '14px', margin: '4px 0 0 0' }}>
                  Record all costs against one invoice line
                </p>
              </div>
             <div style={{ display: 'flex', gap: '8px' }}>
                <button onClick={() => { setMinimized(true); setMaximized(false); }} style={styles.minimizeButton}>➖</button>
                <button
                  onClick={() => { setMaximized((v) => !v); setMinimized(false); }}
                  style={styles.maximizeButton}
                  title={maximized ? 'Restore' : 'Maximize'}
                >
                  {maximized ? '⊡' : '⛶'}
                </button>
                <button onClick={onClose} style={styles.closeButton}>✕</button>
              </div>
            </div>

            <form onSubmit={handleSubmit}>
              <div style={{ padding: '24px 30px' }}>

                {/* ─── Cascading selection ─── */}
                <div style={styles.sectionLabel}>Select Invoice</div>
                <div style={styles.row}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Product <span style={styles.required}>*</span></label>
                    <select value={productId} onChange={(e) => handleProductChange(e.target.value)} style={styles.input} disabled={loadingMaster} required>
                      <option value="">{loadingMaster ? 'Loading...' : '-- Select product --'}</option>
                      {products.map((p) => (<option key={p.id} value={p.id}>{p.code} — {p.name}</option>))}
                    </select>
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Customer <span style={styles.required}>*</span></label>
                    <select value={customerId} onChange={(e) => handleCustomerChange(e.target.value)} style={styles.input} disabled={!productId || loadingCustomers} required>
                      <option value="">
                        {!productId ? '-- Pick product first --'
                          : loadingCustomers ? 'Loading...'
                          : customers.length === 0 ? '-- No customers bought this product --'
                          : '-- Select customer --'}
                      </option>
                      {customers.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
                    </select>
                  </div>
                  <div style={{ ...styles.formGroup, flex: 2 }}>
                    <label style={styles.label}>Invoice <span style={styles.required}>*</span></label>
                    <select value={saleItemId} onChange={(e) => handleSaleLineChange(e.target.value)} style={styles.input} disabled={!customerId || loadingLines} required>
                      <option value="">
                        {!customerId ? '-- Pick customer first --'
                          : loadingLines ? 'Loading...'
                          : saleLines.length === 0 ? '-- No invoices found --'
                          : '-- Select invoice --'}
                      </option>
                      {saleLines.map((l) => (
                        <option key={l.sale_item_id} value={l.sale_item_id}>
                          {l.invoice_no} · {fmtDate(l.invoice_date)} · {fmtQty(l.quantity)}{l.unit} · {fmtMoney(l.line_amount)}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* ─── Auto-generated invoice detail ─── */}
                {loadingDetail && <div style={styles.loadingBox}>⏳ Loading invoice detail...</div>}

                {detail && !loadingDetail && (
                  <div style={styles.detailBox}>
                    <div style={styles.detailTitle}>Invoice {detail.invoice_no} — auto-generated</div>
                    <div style={styles.detailGrid}>
                      <div><span style={styles.detailLabel}>Inv Date</span>{fmtDate(detail.invoice_date)}</div>
                      <div><span style={styles.detailLabel}>Qty</span>{fmtQty(detail.quantity)} {detail.unit}</div>
                      <div><span style={styles.detailLabel}>Rate</span>{fmtMoney(detail.rate)}</div>
                      <div><span style={styles.detailLabel}>Total</span>{fmtMoney(lineAmount)}</div>
                      <div><span style={styles.detailLabel}>GST</span>{fmtMoney(lineGst)}</div>
                      <div><span style={styles.detailLabel}>Total with GST</span><strong style={{ color: '#1565c0' }}>{fmtMoney(lineTotalWithGst)}</strong></div>
                      {cogs > 0 && <div><span style={styles.detailLabel}>Cost of Goods</span><span style={{ color: '#e74c3c' }}>{fmtMoney(cogs)}</span></div>}
                    </div>
                  </div>
                )}

                {/* ─── Expense categories ─── */}
                {detail && !loadingDetail && expenseTypes.map((t) => {
                  const lines = linesByType[t.id] || [];
                  const isFixed = Boolean(t.fixed_labels);
                  return (
                    <div key={t.id} style={styles.categoryCard}>
                      <div style={styles.categoryHeader}>
                        <span style={{ ...styles.categoryDot, background: t.color || '#7f8c8d' }} />
                        <span style={styles.categoryName}>{t.name}</span>
                        <span style={styles.categoryTotal}>{fmtMoney(typeTotal(t.id))}</span>
                      </div>

                      {lines.map((l, i) => (
                        <div key={i} style={styles.lineRow}>
                          <div style={{ flex: 3, minWidth: '200px' }}>
                            <label style={styles.miniLabel}>Detail</label>
                            {isFixed ? (
                              <div style={styles.fixedLabel}>{l.label}</div>
                            ) : (
                              <input
                                type="text"
                                value={l.label}
                                onChange={(e) => handleLineChange(t.id, i, 'label', e.target.value)}
                                style={styles.input}
                                placeholder="e.g. Empty drums, Seals, Water, Gas"
                              />
                            )}
                          </div>
                          <div style={{ flex: 1, minWidth: '120px' }}>
                            <label style={styles.miniLabel}>Amount (Rs)</label>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={l.amount}
                              onChange={(e) => handleLineChange(t.id, i, 'amount', e.target.value)}
                              style={styles.input}
                              placeholder="0"
                            />
                          </div>
                          {!isFixed && (
                            <button
                              type="button"
                              onClick={() => removeLine(t.id, i)}
                              style={styles.removeButton}
                              disabled={lines.length === 1}
                              title="Remove line"
                            >
                              🗑️
                            </button>
                          )}
                        </div>
                      ))}

                      {!isFixed && (
                        <button type="button" onClick={() => addLine(t.id)} style={styles.addLineButton}>
                          + Add line
                        </button>
                      )}
                    </div>
                  );
                })}

                {/* ─── Real Margin Summary ─── */}
                {detail && !loadingDetail && (
                  <div style={styles.totalsBox}>
                    <div style={styles.totalRow}>
                      <span>Revenue (excl. GST)</span>
                      <span style={{ color: '#27ae60' }}>{fmtMoney(lineAmount)}</span>
                    </div>
                    {cogs > 0 && (
                      <div style={styles.totalRow}>
                        <span>Less: Cost of Goods ({fmtQty(detail.quantity)} {detail.unit} × {fmtMoney(detail.batch_cost_per_unit)}/unit)</span>
                        <span style={{ color: '#e74c3c' }}>({fmtMoney(cogs)})</span>
                      </div>
                    )}
                    {expenseTypes.map((t) => (
                      typeTotal(t.id) > 0 && (
                        <div key={t.id} style={styles.totalRow}>
                          <span>Less: {t.name}</span>
                          <span style={{ color: '#e74c3c' }}>({fmtMoney(typeTotal(t.id))})</span>
                        </div>
                      )
                    ))}
                    <div style={styles.totalRowGrand}>
                      <span>Total Deductions</span>
                      <span>({fmtMoney(cogs + grandTotal)})</span>
                    </div>
                    <div style={{
                      ...styles.totalRowNet,
                      color: realMargin >= 0 ? '#27ae60' : '#e74c3c',
                    }}>
                      <span>Real Margin</span>
                      <span>{fmtMoney(realMargin)}</span>
                    </div>
                    {cogs === 0 && (
                      <div style={{ fontSize: 11, color: '#f39c12', marginTop: 6 }}>
                        ⚠️ Batch cost not found — COGS excluded from margin. Check batch has a cost/unit.
                      </div>
                    )}
                  </div>
                )}

              </div>

              <div style={styles.modalFooter}>
                <button type="button" onClick={onClose} style={styles.cancelButton} disabled={submitting}>Cancel</button>
                <button type="submit" style={styles.submitButton} disabled={submitting || !detail}>
                  {submitting ? '⏳ Saving...' : '💾 Save Expenses'}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

const styles = {
  maximizeButton: { padding: '8px 14px', background: '#f0f0f0', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '18px', fontWeight: '700', color: '#666' },
  minimizeButton: { padding: '8px 14px', background: '#f0f0f0', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '16px', fontWeight: '700', color: '#666' },
  minimizedBar: { position: 'fixed', bottom: 0, left: 0, right: 0, display: 'flex', justifyContent: 'flex-end', padding: '0 20px 12px', zIndex: 1000, pointerEvents: 'none' },
  minimizedModal: { background: 'white', borderRadius: '10px 10px 0 0', boxShadow: '0 -4px 20px rgba(0,0,0,0.15)', padding: '12px 20px', cursor: 'pointer', pointerEvents: 'all', minWidth: '260px' },
  minimizedContent: { display: 'flex', alignItems: 'center', gap: '12px' },
  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' },
  overlayMaximized: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'stretch', justifyContent: 'stretch', zIndex: 1000, padding: 0 },
  modal: { background: 'white', borderRadius: '12px', width: '95%', maxWidth: '920px', maxHeight: '92vh', overflow: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' },
  modalMaximized: { background: 'white', borderRadius: 0, width: '100%', height: '100%', overflow: 'auto', boxShadow: 'none' },
  modalHeader: { padding: '20px 30px', borderBottom: '2px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', position: 'sticky', top: 0, background: 'white', zIndex: 10 },
  modalTitle: { fontSize: '22px', fontWeight: '700', color: '#2c3e50', margin: 0 },
  closeButton: { padding: '8px 16px', background: '#f0f0f0', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '20px', fontWeight: '700', color: '#666' },
  sectionLabel: { fontSize: '12px', fontWeight: '700', color: '#7f8c8d', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '4px', marginBottom: '12px', paddingBottom: '6px', borderBottom: '1px solid #f0f0f0' },
  row: { display: 'flex', gap: '16px', flexWrap: 'wrap' },
  formGroup: { flex: 1, minWidth: '180px', display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '12px' },
  label: { fontSize: '13px', fontWeight: '600', color: '#2c3e50' },
  miniLabel: { fontSize: '11px', fontWeight: '600', color: '#7f8c8d', marginBottom: '4px', display: 'block', textTransform: 'uppercase' },
  required: { color: '#e74c3c' },
  input: { width: '100%', padding: '10px 12px', border: '2px solid #e0e0e0', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box', fontFamily: 'inherit' },
  loadingBox: { padding: '20px', textAlign: 'center', color: '#7f8c8d', fontSize: '14px' },
  detailBox: { background: '#e8f4fd', borderRadius: '10px', padding: '14px 18px', marginBottom: '18px', border: '1px solid #bfdff7' },
  detailTitle: { fontSize: '12px', fontWeight: '700', color: '#1565c0', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '10px' },
  detailGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: '10px 16px', fontSize: '14px', color: '#2c3e50' },
  detailLabel: { display: 'block', fontSize: '10px', fontWeight: '700', color: '#7f8c8d', textTransform: 'uppercase', marginBottom: '2px' },
  categoryCard: { background: '#f8f9fa', borderRadius: '10px', padding: '14px 16px', marginBottom: '12px', border: '1px solid #e6e6e6' },
  categoryHeader: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px', paddingBottom: '8px', borderBottom: '1px dashed #ddd' },
  categoryDot: { width: '10px', height: '10px', borderRadius: '50%', flexShrink: 0 },
  categoryName: { fontSize: '14px', fontWeight: '700', color: '#2c3e50', flex: 1 },
  categoryTotal: { fontSize: '14px', fontWeight: '700', color: '#2c3e50' },
  lineRow: { display: 'flex', gap: '10px', alignItems: 'flex-end', marginBottom: '8px', flexWrap: 'wrap' },
  fixedLabel: { padding: '10px 12px', background: 'white', border: '1px solid #e6e6e6', borderRadius: '8px', fontSize: '14px', color: '#2c3e50' },
  removeButton: { padding: '10px 12px', background: '#e74c3c', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', height: '42px' },
  addLineButton: { width: '100%', padding: '9px', background: 'white', color: '#7f8c8d', border: '2px dashed #d5d5d5', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', marginTop: '4px' },
  totalsBox: { background: '#fdf6ec', borderRadius: '10px', padding: '16px 20px', marginTop: '16px', border: '1px solid #f5e0bf' },
  totalRow: { display: 'flex', justifyContent: 'space-between', fontSize: '14px', color: '#7f8c8d', padding: '4px 0' },
  totalRowGrand: { display: 'flex', justifyContent: 'space-between', fontSize: '15px', fontWeight: '700', color: '#2c3e50', padding: '8px 0 4px', borderTop: '1px solid #f0d5a8', marginTop: '4px' },
  totalRowNet: { display: 'flex', justifyContent: 'space-between', fontSize: '17px', fontWeight: '700', padding: '8px 0 4px', borderTop: '2px solid #e8c97a', marginTop: '4px' },
  modalFooter: { padding: '16px 30px', borderTop: '2px solid #f0f0f0', display: 'flex', justifyContent: 'flex-end', gap: '12px', position: 'sticky', bottom: 0, background: 'white' },
  cancelButton: { padding: '12px 24px', background: '#95a5a6', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
  submitButton: { padding: '12px 28px', background: '#e74c3c', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
};

export default InvoiceExpenseFormModal;